import { useEffect, useState, type FormEvent } from "react";
import { useLocation, useRoute } from "wouter";
import { AuthService } from "../lib/auth";
import { queryClient } from "../lib/queryClient";
import { startRecommendedStudentBriefing } from "../lib/student-task-briefing";

const COLORS = {
  bg: "#FAFAF7",
  surface: "#FFFFFF",
  ink: "#18181B",
  muted: "#71717A",
  line: "#E4E4E7",
  rose: "#BE123C",
  roseSoft: "#FFF1F2",
};

const FONT = '"Hiragino Sans", "Noto Sans JP", "Yu Gothic", "Meiryo", system-ui, sans-serif';

export default function GuestEntry() {
  const [, params] = useRoute("/g/:token");
  const token = params?.token || "";
  const [, setLocation] = useLocation();
  const [label, setLabel] = useState("");
  const [expiresAt, setExpiresAt] = useState<string | null>(null);
  const [displayName, setDisplayName] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!token) {
        setError("入場コードがありません。");
        setLoading(false);
        return;
      }
      try {
        const preview = await AuthService.getInstance().previewGuestPass(token);
        if (cancelled) return;
        setLabel(preview.label);
        setExpiresAt(preview.expiresAt);
        setError("");
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : "このQRは使えません。");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!token || submitting) return;
    setSubmitting(true);
    setError("");
    try {
      await AuthService.getInstance().startGuestEntry({
        token,
        displayName: displayName.trim() || undefined,
      });
      queryClient.invalidateQueries({ queryKey: ["authStatus"] });
      queryClient.invalidateQueries({ queryKey: ["currentUser"] });
      queryClient.invalidateQueries({ queryKey: ["auth", "me"] });
      const briefingPath = await startRecommendedStudentBriefing({ hideBack: true });
      setLocation(briefingPath);
    } catch (err) {
      setError(err instanceof Error ? err.message : "ゲスト入場に失敗しました。");
    } finally {
      setSubmitting(false);
    }
  };

  const expiryLabel = expiresAt
    ? new Date(expiresAt).toLocaleString("ja-JP", {
        month: "numeric",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : null;

  return (
    <div
      style={{
        minHeight: "100vh",
        background: COLORS.bg,
        fontFamily: FONT,
        color: COLORS.ink,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 440,
          background: COLORS.surface,
          border: `1px solid ${COLORS.line}`,
          borderRadius: 16,
          padding: 28,
        }}
      >
        <div
          style={{
            fontSize: 10,
            fontWeight: 800,
            letterSpacing: "0.15em",
            color: COLORS.muted,
            marginBottom: 10,
          }}
        >
          GUEST PASS
        </div>
        <h1 style={{ fontSize: 18, fontWeight: 800, letterSpacing: -0.3, margin: 0 }}>
          ゲスト入場
        </h1>
        <p style={{ fontSize: 13.5, lineHeight: 1.75, color: COLORS.muted, marginTop: 8, marginBottom: 0 }}>
          {loading
            ? "入場券を確認しています…"
            : label
              ? `「${label}」で面接を始めます。名前は任意です。`
              : "このQRでは入場できません。"}
        </p>
        {expiryLabel && !error && (
          <p style={{ fontSize: 12.5, color: COLORS.muted, marginTop: 8 }}>
            有効期限 {expiryLabel}
          </p>
        )}

        {error && (
          <div
            style={{
              marginTop: 16,
              padding: "10px 12px",
              background: COLORS.roseSoft,
              color: COLORS.rose,
              fontSize: 12.5,
              lineHeight: 1.6,
              borderRadius: 8,
            }}
          >
            {error}
          </div>
        )}

        {!loading && label && (
          <form onSubmit={handleSubmit} style={{ marginTop: 22 }}>
            <label style={{ display: "block" }}>
              <span style={{ display: "block", fontSize: 12.5, fontWeight: 800, marginBottom: 6 }}>
                お名前（任意）
              </span>
              <input
                value={displayName}
                onChange={(event) => setDisplayName(event.target.value)}
                maxLength={40}
                placeholder="空欄なら自動で付けます"
                style={{
                  width: "100%",
                  height: 44,
                  border: `1px solid ${COLORS.line}`,
                  borderRadius: 10,
                  padding: "0 12px",
                  fontSize: 14,
                  fontFamily: FONT,
                  color: COLORS.ink,
                  background: COLORS.surface,
                  outline: "none",
                }}
              />
            </label>
            <button
              type="submit"
              disabled={submitting}
              style={{
                marginTop: 16,
                width: "100%",
                height: 48,
                border: 0,
                borderRadius: 999,
                color: "#FFFFFF",
                fontSize: 14,
                fontWeight: 800,
                fontFamily: FONT,
                cursor: submitting ? "wait" : "pointer",
                background: submitting
                  ? "linear-gradient(135deg, #D4D4D8 0%, #A1A1AA 100%)"
                  : "linear-gradient(135deg, #FF8A3D 0%, #FF6B1A 100%)",
              }}
            >
              {submitting ? "入場しています…" : "面接をはじめる"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
